import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import logger from '../../utils/logger';
import packageJson from '../../../package.json';
import './VersionInfoPage.css';

const VersionInfoPage = ({ onBack }) => {
  const [buildInfo, setBuildInfo] = useState(null);
  const { t } = useI18n();

  // Fetch build info from meta.json
  useEffect(() => {
    // Create AbortController to cancel fetch on unmount
    const abortController = new AbortController();

    const fetchBuildInfo = async () => {
      try {
        const response = await fetch('/meta.json', {
          signal: abortController.signal,
        });
        if (response.ok) {
          const data = await response.json();
          setBuildInfo(data);
        }
      } catch (error) {
        // Ignore AbortError (expected when component unmounts)
        if (error.name !== 'AbortError') {
          logger.debug('Could not fetch build info:', error);
        }
      }
    };
    fetchBuildInfo();

    // Cleanup: abort fetch on unmount
    return () => {
      abortController.abort();
    };
  }, []);

  const formatBuildTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const buildVersion = buildInfo?.commitId;
  const buildDate = buildInfo?.buildTime ? formatBuildTime(buildInfo.buildTime) : null;

  const versionInfoItems = [
    {
      label: t('SessionsTableView.appVer', {}, 'App Version'),
      value: `v${packageJson.version}`,
    },
  ];

  if (buildVersion) {
    versionInfoItems.push({
      label: t('DeveloperInfoPage.buildVersion', {}, 'Build Version'),
      value: buildVersion,
    });
  }

  if (buildDate) {
    versionInfoItems.push({
      label: t('DeveloperInfoPage.buildDate', {}, 'Build Date'),
      value: buildDate,
    });
  }

  return (
    <div className="version-info-page">
      <div className="version-info-header">
        <button
          className="version-info-header-button"
          onClick={onBack}
          aria-label={t('SignInForm.goBack', {}, 'Go Back')}
          title={t('SignInForm.goBack', {}, 'Go Back')}
        >
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="version-info-header-title">
          {t('SettingsView.versionInfo', {}, 'Version Info')}
        </span>
        <div className="version-info-header-spacer" />
      </div>
      <div className="version-info-content">
        <div className="info-section">
          <div className="info-section-header">
            {t('DeveloperView.appInfo', {}, 'App Info')}
          </div>
          {versionInfoItems.map((item, index) => (
            <div className="info-item" key={`version-${index}`}>
              <div className="info-label">{item.label}</div>
              <div className="info-value">{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

VersionInfoPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default VersionInfoPage;

